/**
 * Page d'accueil CNIL — Moteur de recherche + liste des délibérations
 * Route : /cnil/deliberations
 */

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation, Trans } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';

const _SITE_URL = 'https://smartlawyer.ai';
const LIMIT    = 20;

// ── Types ──────────────────────────────────────────────────────────────────────

interface CnilDelib {
  id:            string;
  score?:        number;
  cnil_id?:      string;
  num:           string;
  nor?:          string;
  nature?:       string;
  nature_delib?: string;
  etat?:         string;
  titre?:        string;
  titre_long?:   string;
  date_texte?:   string;
  date_publi?:   string;
  extrait?:      string;
  texte_complet?: string;
  legifrance_url?: string;
}

// ── Constantes filtres ─────────────────────────────────────────────────────────

// `label` = clé i18n (cnil.filters.*), traduite au rendu — `value` envoyée à l'API
const NATURES_DELIB = [
  { value: '',            label: 'cnil.filters.all_categories' },
  { value: 'Sanction',   label: 'cnil.filters.sanctions' },
  { value: 'Avis',       label: 'cnil.filters.opinions' },
  { value: 'Autorisation', label: 'cnil.filters.authorizations' },
  { value: 'Recommandation', label: 'cnil.filters.recommendations' },
  { value: 'Délibération', label: 'cnil.filters.deliberations' },
];

const ETATS = [
  { value: '',        label: 'cnil.filters.all_statuses' },
  { value: 'VIGUEUR', label: 'cnil.status.in_force' },
  { value: 'ABROGE',  label: 'cnil.status.repealed' },
];

// Valeurs API du champ nature → clé i18n (cnil.nature.*) ; fallback : valeur brute
const NATURE_KEYS: Record<string, string> = {
  Sanction:       'cnil.nature.sanction',
  Avis:           'cnil.nature.opinion',
  Autorisation:   'cnil.nature.authorization',
  Recommandation: 'cnil.nature.recommendation',
  'Délibération': 'cnil.nature.deliberation',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(d?: string): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
}

function EtatBadge({ etat }: { etat?: string }) {
  const { t } = useTranslation();
  if (!etat) return null;
  const isVigueur = etat === 'VIGUEUR';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
      isVigueur
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      {isVigueur ? t('cnil.status.in_force') : t('cnil.status.repealed')}
    </span>
  );
}

function NatureBadge({ nature }: { nature?: string }) {
  const { t } = useTranslation();
  if (!nature) return null;
  const colors: Record<string, string> = {
    Sanction:       'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    Avis:           'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    Autorisation:   'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    Recommandation: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  };
  const cls = colors[nature] ?? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${cls}`}>
      {NATURE_KEYS[nature] ? t(NATURE_KEYS[nature]) : nature}
    </span>
  );
}

// ── Card délibération ──────────────────────────────────────────────────────────

function CnilCard({ d }: { d: CnilDelib }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/cnil/deliberations/${encodeURIComponent(d.num)}`}
      className="block group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:border-violet-400 dark:hover:border-violet-500 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-mono text-violet-600 dark:text-violet-400 font-semibold">
            {t('cnil.card.number', { num: d.num })}
          </span>
          <NatureBadge nature={d.nature_delib ?? d.nature} />
          <EtatBadge etat={d.etat} />
        </div>
        {d.date_texte && (
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
            {formatDate(d.date_texte)}
          </span>
        )}
      </div>

      <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug mb-2 group-hover:text-violet-700 dark:group-hover:text-violet-300 transition-colors line-clamp-2">
        {d.titre_long || d.titre || t('cnil.card.default_title', { num: d.num })}
      </h2>

      {d.extrait && (
        <p className="text-xs text-muted-foreground line-clamp-3 leading-relaxed">
          {d.extrait}
        </p>
      )}
    </Link>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function PublicCnil() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode]               = useState<'browse' | 'search'>('browse');
  const [query, setQuery]             = useState(searchParams.get('q') || '');
  const [natureDelib, setNatureDelib] = useState(searchParams.get('nature') || '');
  const [etat, setEtat]               = useState(searchParams.get('etat') || '');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  // Browse
  const [browseResults, setBrowseResults] = useState<CnilDelib[]>([]);
  const [browseTotal, setBrowseTotal]     = useState(0);
  const [page, setPage]                   = useState(0);

  // Search
  const [searchResults, setSearchResults] = useState<CnilDelib[]>([]);
  const [searchTime, setSearchTime]       = useState<number | null>(null);

  // ── Browse ──────────────────────────────────────────────────────────────────
  const fetchBrowse = useCallback(async (pageNum: number, nd: string, et: string) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit:  String(LIMIT),
      offset: String(pageNum * LIMIT),
    });
    if (nd) params.set('nature_delib', nd);
    if (et) params.set('etat', et);

    try {
      const res  = await fetch(`/api/cnil/deliberations?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBrowseResults(data.results || []);
      setBrowseTotal(data.total || 0);
    } catch {
      setError(t('cnil.errors.load'));
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Search ──────────────────────────────────────────────────────────────────
  const doSearch = useCallback(async (q: string, nd: string, et: string) => {
    if (!q.trim()) return;
    setLoading(true);
    setError(null);
    setSearchResults([]);
    const t0 = performance.now();

    try {
      const res  = await fetch('/api/cnil/search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: q, limit: 50,
          nature_delib: nd || undefined,
          etat:         et || undefined,
        }),
      });
      const data = await res.json();
      setSearchResults(data.results || []);
      setSearchTime(Math.round(performance.now() - t0));
    } catch {
      setError(t('cnil.errors.search'));
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const q  = searchParams.get('q') || '';
    const nd = searchParams.get('nature') || '';
    const et = searchParams.get('etat') || '';
    setQuery(q); setNatureDelib(nd); setEtat(et);
    if (q) { setMode('search'); doSearch(q, nd, et); }
    else   { setMode('browse'); fetchBrowse(0, nd, et); }
  }, []);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) {
      setMode('browse'); setPage(0);
      setSearchParams(buildParams('', natureDelib, etat));
      fetchBrowse(0, natureDelib, etat);
      return;
    }
    setMode('search');
    setSearchParams(buildParams(query, natureDelib, etat));
    doSearch(query, natureDelib, etat);
  };

  const handleFilterChange = (nd: string, et: string) => {
    setNatureDelib(nd); setEtat(et);
    setPage(0);
    if (mode === 'search' && query.trim()) {
      setSearchParams(buildParams(query, nd, et));
      doSearch(query, nd, et);
    } else {
      setMode('browse');
      setSearchParams(buildParams('', nd, et));
      fetchBrowse(0, nd, et);
    }
  };

  function buildParams(q: string, nd: string, et: string) {
    const p: Record<string, string> = {};
    if (q)  p.q      = q;
    if (nd) p.nature = nd;
    if (et) p.etat   = et;
    return p;
  }

  const handlePageChange = (delta: number) => {
    const newPage = page + delta;
    if (newPage < 0) return;
    setPage(newPage);
    fetchBrowse(newPage, natureDelib, etat);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const results  = mode === 'search' ? searchResults : browseResults;
  const total    = mode === 'search' ? searchResults.length : browseTotal;
  const hasPrev  = mode === 'browse' && page > 0;
  const hasNext  = mode === 'browse' && browseResults.length === LIMIT;

  return (
    <>
      <Helmet>
        <title>{t('cnil.meta.title')}</title>
        <meta name="description" content={t('cnil.meta.description')} />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        <PublicHeader />

        <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">

          {/* ── Hero ─────────────────────────────────────────────────────────── */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-3xl">🏛️</span>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {t('cnil.hero.title')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('cnil.hero.subtitle')}
                </p>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Link to="/" className="hover:underline">{t('cjue.breadcrumb.home')}</Link>
              <span>/</span>
              <Link to="/jurisprudence" className="hover:underline">{t('nav.jurisprudence')}</Link>
              <span>/</span>
              <span className="text-violet-600 dark:text-violet-400">{t('nav.cnil')}</span>
            </div>
          </div>

          {/* ── Barre de recherche ───────────────────────────────────────────── */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('cnil.search.placeholder')}
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                className="px-5 py-2.5 text-sm font-semibold bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
              >
                {t('common.search')}
              </button>
              {mode === 'search' && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery(''); setMode('browse'); setPage(0);
                    setSearchParams(buildParams('', natureDelib, etat));
                    fetchBrowse(0, natureDelib, etat);
                  }}
                  aria-label={t('cnil.search.clear')}
                  className="px-3 py-2.5 text-sm text-muted-foreground hover:text-gray-900 dark:hover:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-lg transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </form>

          {/* ── Filtres ─────────────────────────────────────────────────────── */}
          <div className="flex flex-wrap gap-2 mb-6">
            {/* Filtre nature */}
            <select
              value={natureDelib}
              onChange={e => handleFilterChange(e.target.value, etat)}
              className="text-xs px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              {NATURES_DELIB.map(n => (
                <option key={n.value} value={n.value}>{t(n.label)}</option>
              ))}
            </select>

            {/* Filtre état */}
            <select
              value={etat}
              onChange={e => handleFilterChange(natureDelib, e.target.value)}
              className="text-xs px-3 py-1.5 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              {ETATS.map(e => (
                <option key={e.value} value={e.value}>{t(e.label)}</option>
              ))}
            </select>

            {/* Compteur */}
            <span className="ml-auto text-xs text-muted-foreground self-center">
              {mode === 'search' && searchTime != null
                ? t('cnil.results.search_count', { count: total, ms: searchTime })
                : t('cnil.results.browse_count', { count: total, formatted: total.toLocaleString(getDateLocale()) })
              }
            </span>
          </div>

          {/* ── Contenu ─────────────────────────────────────────────────────── */}
          {loading ? (
            <div className="flex justify-center py-16"><Loader /></div>
          ) : error ? (
            <div className="text-center py-16 text-red-500">{error}</div>
          ) : results.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              {mode === 'search'
                ? t('cnil.empty.no_results')
                : t('cnil.empty.none_available')
              }
            </div>
          ) : (
            <div className="grid gap-3">
              {results.map(d => <CnilCard key={d.id} d={d} />)}
            </div>
          )}

          {/* ── Pagination browse ────────────────────────────────────────────── */}
          {!loading && mode === 'browse' && (hasPrev || hasNext) && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => handlePageChange(-1)}
                disabled={!hasPrev}
                className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                ← {t('common.previous')}
              </button>
              <span className="text-sm text-muted-foreground">{t('cjue.results.page', { page: page + 1 })}</span>
              <button
                onClick={() => handlePageChange(1)}
                disabled={!hasNext}
                className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                {t('common.next')} →
              </button>
            </div>
          )}

          {/* ── Lien vers jurisprudence RGPD ─────────────────────────────────── */}
          <div className="mt-12 p-5 bg-violet-50 dark:bg-violet-900/10 border border-violet-200 dark:border-violet-800 rounded-xl text-center">
            <p className="text-sm text-violet-800 dark:text-violet-300 mb-3">
              🔗 <Trans i18nKey="cnil.rgpd_link.text" components={{ strong: <strong /> }} />
            </p>
            <Link
              to="/jurisprudence/rgpd"
              className="text-sm font-semibold text-violet-600 hover:text-violet-800 dark:text-violet-400 dark:hover:text-violet-200 underline underline-offset-2"
            >
              {t('cnil.rgpd_link.cta')} →
            </Link>
          </div>

        </main>

        <PublicFooter />
      </div>
    </>
  );
}
